import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Mail, Phone, Building2, Handshake, Activity as ActIcon, Users, Pencil, Trash2, Linkedin } from "lucide-react";
import api, { formatApiError } from "../api";
import { useAuth, can } from "../auth";
import { Badge, Spinner, Button, Modal, Field, Input, Textarea, Select, ImageWithFallback } from "../components/common";
import { formatMoney } from "../format";

const REFERRER_TAG = "referrer";
const STATUSES = ["lead", "prospect", "customer", "inactive"];
const emptyForm = { first_name: "", last_name: "", email: "", phone: "", title: "", status: "lead", company_id: "", linkedin_url: "", notes: "" };

export default function ContactDetail() {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const writable = can.write(user);
  const [data, setData] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [modal, setModal] = useState(false);
  const [email, setEmail] = useState({ direction: "outbound", subject: "", body: "" });
  const [error, setError] = useState("");
  const [editModal, setEditModal] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(() => {
    api.get(`/contacts/${id}/detail`).then((r) => setData(r.data)).catch(() => navigate("/contacts"));
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/companies").then((r) => setCompanies(r.data)); }, []);

  const openEdit = () => {
    const c = data.contact;
    setForm({ ...emptyForm, ...c, company_id: c.company_id || "" });
    setEditModal(true);
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const saveEdit = async () => {
    await api.put(`/contacts/${id}`, { ...form, company_id: form.company_id || null });
    setEditModal(false); load();
  };
  const delContact = async () => {
    if (!window.confirm(t("common.confirmDelete"))) return;
    await api.delete(`/contacts/${id}`);
    navigate("/contacts");
  };

  const toggleReferrer = async () => {
    const c = data.contact;
    const tags = c.tags || [];
    const nextTags = tags.includes(REFERRER_TAG)
      ? tags.filter((tg) => tg !== REFERRER_TAG)
      : [...tags, REFERRER_TAG];
    try {
      await api.put(`/contacts/${id}`, { ...c, tags: nextTags });
      load();
    } catch (e) {
      console.error("Contact update failed:", e);
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const logEmail = async () => {
    await api.post("/activities", {
      type: "email",
      direction: email.direction,
      subject: email.subject,
      description: email.body,
      contact_id: id,
    });
    setModal(false);
    setEmail({ direction: "outbound", subject: "", body: "" });
    load();
  };

  if (!data) return <Spinner />;
  const c = data.contact;

  return (
    <div className="space-y-6">
      <button onClick={() => navigate("/contacts")} data-testid="detail-back" className="flex items-center gap-1 text-sm text-muted hover:text-txt transition-colors">
        <ArrowLeft size={16} /> {t("detail.back")}
      </button>

      {error && <p className="text-sm text-danger">{error}</p>}

      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <ImageWithFallback src={c.gravatar_url} size={56}
            fallback={<span className="text-xl font-bold">{c.first_name?.[0]?.toUpperCase()}</span>}
            fallbackClassName="bg-primary/15 text-primary" />
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{c.first_name} {c.last_name}</h1>
            <div className="text-sm text-muted">{c.title}{c.company_name && ` · ${c.company_name}`}</div>
            <div className="mt-2 flex items-center gap-2">
              <Badge value={c.status} label={t(`statuses.${c.status}`)} />
              {(c.tags || []).includes(REFERRER_TAG) && <Badge value="won" label={t("contact.regularReferrer")} />}
            </div>
          </div>
        </div>
        {writable && (
          <div className="flex gap-1 shrink-0">
            <button onClick={openEdit} data-testid="edit-contact-btn" className="p-1.5 rounded-sm hover:bg-border text-muted transition-colors"><Pencil size={16} /></button>
            <button onClick={delContact} data-testid="delete-contact-btn" className="p-1.5 rounded-sm hover:bg-danger/15 text-muted hover:text-danger transition-colors"><Trash2 size={16} /></button>
          </div>
        )}
      </div>

      <div className="border border-border rounded-sm p-5 space-y-2">
        <h3 className="font-display font-bold mb-2">{t("detail.overview")}</h3>
        {c.email && <div className="flex items-center gap-2 text-sm"><Mail size={14} className="text-muted" />{c.email}</div>}
        {c.phone && <div className="flex items-center gap-2 text-sm"><Phone size={14} className="text-muted" />{c.phone}</div>}
        {c.company_name && <div className="flex items-center gap-2 text-sm"><Building2 size={14} className="text-muted" />{c.company_name}</div>}
        {c.linkedin_url && (
          <a href={c.linkedin_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-primary hover:underline w-fit">
            <Linkedin size={14} />{t("contact.linkedinUrl")}
          </a>
        )}
        {c.notes && <p className="text-sm text-muted pt-2 border-t border-border">{c.notes}</p>}
        {writable && (
          <label className="flex items-center gap-2 text-sm pt-2 border-t border-border">
            <input type="checkbox" data-testid="regular-referrer-toggle"
              checked={(c.tags || []).includes(REFERRER_TAG)} onChange={toggleReferrer} />
            {t("contact.regularReferrer")}
          </label>
        )}
      </div>

      <div className="border border-border rounded-sm p-5">
        <h3 className="font-display font-bold mb-3 flex items-center gap-2"><Handshake size={16} className="text-primary" />{t("detail.deals")}</h3>
        {data.deals.length === 0 ? <p className="text-sm text-muted">{t("detail.noItems")}</p> : (
          <div className="space-y-2">
            {data.deals.map((d) => (
              <Link to="/deals" key={d.id} className="flex items-center justify-between text-sm hover:text-primary transition-colors">
                <span className="truncate">{d.title}</span>
                <span className="flex items-center gap-2 shrink-0"><span className="font-medium">{formatMoney(d.value, d.currency)}</span><Badge value={d.stage} label={t(`statuses.${d.stage}`)} /></span>
              </Link>
            ))}
          </div>
        )}
      </div>

      <div className="border border-border rounded-sm p-5" data-testid="referrals-panel">
        <h3 className="font-display font-bold mb-3 flex items-center gap-2"><Users size={16} className="text-primary" />{t("contact.referrals")}</h3>
        <div className="flex gap-6">
          <div>
            <div className="font-display text-2xl font-bold">{data.referrals.count}</div>
            <div className="text-xs text-muted">{t("contact.referralsCount")}</div>
          </div>
          <div>
            <div className="font-display text-2xl font-bold">{data.referrals.won_count}</div>
            <div className="text-xs text-muted">{t("contact.referralsWon")}</div>
          </div>
        </div>
      </div>

      <div className="border border-border rounded-sm p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-bold flex items-center gap-2"><ActIcon size={16} className="text-glow" />{t("detail.timeline")}</h3>
          {writable && (
            <Button variant="subtle" onClick={() => setModal(true)} data-testid="log-email-btn" className="py-1.5 px-3 text-xs">
              <Mail size={13} /> {t("email.log")}
            </Button>
          )}
        </div>
        {data.activities.length === 0 ? <p className="text-sm text-muted">{t("detail.noActivities")}</p> : (
          <div className="space-y-3">
            {data.activities.map((a) => (
              <div key={a.id} className="flex items-start gap-3">
                <span className="mt-1.5 w-2 h-2 rounded-full bg-primary shrink-0" />
                <div>
                  <div className="text-sm">{a.subject}</div>
                  <div className="text-xs text-muted">{t(`statuses.${a.type}`)}{a.direction ? ` · ${t(`activity.direction.${a.direction}`)}` : ""}{a.completed ? " · ✓" : ""}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Modal open={modal} onClose={() => setModal(false)} title={t("email.log")}
        footer={<>
          <Button variant="ghost" onClick={() => setModal(false)}>{t("common.cancel")}</Button>
          <Button onClick={logEmail} data-testid="save-email-btn">{t("common.save")}</Button>
        </>}>
        <Field label={t("email.direction")}>
          <Select data-testid="email-direction" value={email.direction} onChange={(e) => setEmail({ ...email, direction: e.target.value })}>
            <option value="outbound">{t("email.outbound")}</option>
            <option value="inbound">{t("email.inbound")}</option>
          </Select>
        </Field>
        <Field label={t("email.subject")}><Input data-testid="email-subject" value={email.subject} onChange={(e) => setEmail({ ...email, subject: e.target.value })} /></Field>
        <Field label={t("email.body")}><Textarea data-testid="email-body" rows={4} value={email.body} onChange={(e) => setEmail({ ...email, body: e.target.value })} /></Field>
      </Modal>

      <Modal open={editModal} onClose={() => setEditModal(false)} title={t("contact.editContact")}
        footer={<>
          <Button variant="ghost" onClick={() => setEditModal(false)}>{t("common.cancel")}</Button>
          <Button onClick={saveEdit} data-testid="save-contact-btn">{t("common.save")}</Button>
        </>}>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("contact.first_name")}><Input data-testid="contact-first-name" value={form.first_name} onChange={set("first_name")} /></Field>
          <Field label={t("contact.last_name")}><Input value={form.last_name || ""} onChange={set("last_name")} /></Field>
        </div>
        <Field label={t("contact.email")}><Input type="email" value={form.email || ""} onChange={set("email")} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("contact.phone")}><Input value={form.phone || ""} onChange={set("phone")} /></Field>
          <Field label={t("contact.jobtitle")}><Input value={form.title || ""} onChange={set("title")} /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("contact.status")}>
            <Select value={form.status} onChange={set("status")}>
              {STATUSES.map((s) => <option key={s} value={s}>{t(`statuses.${s}`)}</option>)}
            </Select>
          </Field>
          <Field label={t("contact.company")}>
            <Select value={form.company_id || ""} onChange={set("company_id")}>
              <option value="">—</option>
              {companies.map((co) => <option key={co.id} value={co.id}>{co.name}</option>)}
            </Select>
          </Field>
        </div>
        <Field label={t("contact.linkedinUrl")}><Input value={form.linkedin_url || ""} onChange={set("linkedin_url")} placeholder="https://linkedin.com/in/…" /></Field>
        <Field label={t("contact.notes")}><Textarea rows={3} value={form.notes || ""} onChange={set("notes")} /></Field>
      </Modal>
    </div>
  );
}
